import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import MindMapNode from './MindMapNode';
import MindMapShape from './MindMapShape';
import './MindMap.css';
import { colorClass } from './palette';
import { buildChildrenMap, getDescendantIds, getVisibleNodes } from './tree';
import { SHAPE_MAX, SHAPE_MIN, clampZoom, computeFitView, edgePath } from './layout';

const GRID = 24;
const MOUSE_SLOP = 5;
const TOUCH_SLOP = 10;
const ACTION_SLOP = 24;
const DOUBLE_TAP_MS = 320;
const REVEAL_MARGIN = 40;
const SHAPE_REVEAL_OFFSET = 32;
const FIT_PADDING = { top: 48, right: 32, bottom: 104, left: 32 };

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const MindMapCanvas = forwardRef(function MindMapCanvas(
  {
    nodes, shapes, sizes, selectedId, selectedShapeId, editingId, editingShapeId,
    onSelect, onEdit, onToggleCollapse, onAddChild, onTextChange, onCommitEdit, onBeginMove, onMoveNodes, onSize,
    onSelectShape, onEditShape, onShapeChange, onMoveShape, onToggleShapeItem,
  },
  ref
) {
  const containerRef = useRef(null);
  const [view, setView] = useState({ x: 0, y: 0, k: 1 });
  const viewRef = useRef(view);
  const pointers = useRef(new Map());
  const gesture = useRef(null);
  const lastTap = useRef({ id: null, time: 0 });
  const fitted = useRef(false);

  const childrenMap = useMemo(() => buildChildrenMap(nodes), [nodes]);
  const visibleNodes = useMemo(() => getVisibleNodes(nodes, childrenMap), [nodes, childrenMap]);
  const byId = useMemo(() => new Map(nodes.map((node) => [node.id, node])), [nodes]);

  const latest = useRef({});
  latest.current = {
    nodes, shapes, visibleNodes, sizes, childrenMap,
    onSelect, onEdit, onToggleCollapse, onAddChild, onBeginMove, onMoveNodes,
    onSelectShape, onEditShape, onMoveShape, onToggleShapeItem,
  };

  const updateView = useCallback((next) => {
    viewRef.current = next;
    setView(next);
  }, []);

  const viewport = () => {
    const rect = containerRef.current.getBoundingClientRect();
    return { w: rect.width, h: rect.height, left: rect.left, top: rect.top };
  };

  const zoomAt = useCallback(
    (cx, cy, factor) => {
      const { x, y, k } = viewRef.current;
      const nextK = clampZoom(k * factor);
      const ratio = nextK / k;
      updateView({ k: nextK, x: cx - (cx - x) * ratio, y: cy - (cy - y) * ratio });
    },
    [updateView]
  );

  const fit = useCallback(() => {
    const { visibleNodes: visible, sizes: measured, shapes: notes } = latest.current;
    const next = computeFitView(visible, measured, viewport(), FIT_PADDING, notes);
    if (next) updateView(next);
  }, [updateView]);

  useImperativeHandle(
    ref,
    () => ({
      fit,
      zoomBy: (factor) => {
        const { w, h } = viewport();
        zoomAt(w / 2, h / 2, factor);
      },
      viewportCenter: () => {
        const { w, h } = viewport();
        const { x, y, k } = viewRef.current;
        return { x: (w / 2 - x) / k, y: (h / 2 - y) / k };
      },
      reveal: (id, { top = 0, bottom = 0 } = {}) => {
        const node = latest.current.nodes.find((candidate) => candidate.id === id);
        const shape = node ? null : latest.current.shapes.find((candidate) => candidate.id === id);
        if (!node && !shape) return;
        const worldY = node ? node.y : shape.y + SHAPE_REVEAL_OFFSET;
        const { x, y, k } = viewRef.current;
        const screenY = worldY * k + y;
        const bandTop = top + REVEAL_MARGIN;
        const bandBottom = Math.max(bandTop, viewport().h - bottom - REVEAL_MARGIN);
        if (screenY >= bandTop && screenY <= bandBottom) return;
        updateView({ k, x, y: y + (bandTop + bandBottom) / 2 - screenY });
      },
    }),
    [fit, zoomAt, updateView]
  );

  useEffect(() => {
    if (fitted.current || !visibleNodes.length) return;
    if (!visibleNodes.every((node) => sizes[node.id])) return;
    fitted.current = true;
    fit();
  }, [visibleNodes, sizes, fit]);

  useEffect(() => {
    const container = containerRef.current;
    const onWheel = (event) => {
      if (event.target.closest('.mm-shape-editing')) return;
      event.preventDefault();
      const rect = container.getBoundingClientRect();
      if (event.ctrlKey || event.metaKey) {
        zoomAt(event.clientX - rect.left, event.clientY - rect.top, Math.exp(-event.deltaY * 0.01));
      } else {
        const { x, y, k } = viewRef.current;
        updateView({ k, x: x - event.deltaX, y: y - event.deltaY });
      }
    };
    container.addEventListener('wheel', onWheel, { passive: false });
    return () => container.removeEventListener('wheel', onWheel);
  }, [updateView, zoomAt]);

  // Focus inside the same user gesture so mobile browsers open the keyboard.
  const focusEditor = (selectAll) => {
    const field = [
      ...containerRef.current.querySelectorAll('.mm-edit textarea, .mm-shape-editing textarea, .mm-shape-editing [data-item-input]'),
    ].at(-1);
    if (!field) return;
    field.focus({ preventScroll: true });
    if (selectAll) field.select();
    else field.setSelectionRange(field.value.length, field.value.length);
  };

  const startPinch = () => {
    const [a, b] = [...pointers.current.values()];
    const rect = containerRef.current.getBoundingClientRect();
    gesture.current = {
      type: 'pinch',
      distance: Math.hypot(a.x - b.x, a.y - b.y) || 1,
      centerX: (a.x + b.x) / 2 - rect.left,
      centerY: (a.y + b.y) / 2 - rect.top,
      view: viewRef.current,
    };
  };

  const handlePointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const target = event.target;
    const resizeHandle = target.closest('[data-action="resize"]');
    if (target.closest('textarea, input')) return;
    if (target.closest('.mm-shape-editing') && !resizeHandle) return;

    containerRef.current.setPointerCapture(event.pointerId);
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (pointers.current.size === 2) {
      startPinch();
      return;
    }
    if (pointers.current.size > 2) return;

    const actionElement = target.closest('[data-action]');
    const base = {
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
      slop: event.pointerType === 'mouse' ? MOUSE_SLOP : TOUCH_SLOP,
      action: resizeHandle ? null : actionElement?.dataset.action,
      itemId: actionElement?.dataset.itemId,
    };

    const shapeElement = target.closest('[data-shape-id]');
    if (shapeElement) {
      const shape = latest.current.shapes.find((candidate) => candidate.id === shapeElement.dataset.shapeId);
      gesture.current = { ...base, type: resizeHandle ? 'resize' : 'shape', id: shape.id, origin: shape };
      return;
    }

    const nodeElement = target.closest('[data-node-id]');
    if (!nodeElement) {
      gesture.current = { ...base, type: 'pan', view: viewRef.current };
      return;
    }

    const id = nodeElement.dataset.nodeId;
    const { nodes: current, childrenMap: children } = latest.current;
    const moving = [id, ...getDescendantIds(current, id, children)];
    const origin = Object.fromEntries(
      moving.map((movingId) => {
        const node = current.find((candidate) => candidate.id === movingId);
        return [movingId, { x: node.x, y: node.y }];
      })
    );
    gesture.current = { ...base, type: 'node', id, origin };
  };

  const handlePointerMove = (event) => {
    if (!pointers.current.has(event.pointerId)) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const current = gesture.current;
    if (!current) return;

    if (current.type === 'pinch' && pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      const rect = containerRef.current.getBoundingClientRect();
      const cx = (a.x + b.x) / 2 - rect.left;
      const cy = (a.y + b.y) / 2 - rect.top;
      const k = clampZoom(current.view.k * (Math.hypot(a.x - b.x, a.y - b.y) / current.distance));
      const ratio = k / current.view.k;
      updateView({
        k,
        x: cx - (current.centerX - current.view.x) * ratio,
        y: cy - (current.centerY - current.view.y) * ratio,
      });
      return;
    }
    if (current.action) return;

    const dx = event.clientX - current.startX;
    const dy = event.clientY - current.startY;
    if (!current.moved) {
      if (Math.hypot(dx, dy) < current.slop) return;
      current.moved = true;
      if (current.type === 'node') {
        latest.current.onSelect(current.id);
        latest.current.onBeginMove();
      } else if (current.type === 'shape' || current.type === 'resize') {
        latest.current.onSelectShape(current.id);
        latest.current.onBeginMove();
      }
    }

    const k = viewRef.current.k;
    if (current.type === 'pan') {
      updateView({ k: current.view.k, x: current.view.x + dx, y: current.view.y + dy });
    } else if (current.type === 'node') {
      latest.current.onMoveNodes(
        Object.fromEntries(
          Object.entries(current.origin).map(([id, position]) => [id, { x: position.x + dx / k, y: position.y + dy / k }])
        )
      );
    } else if (current.type === 'shape') {
      latest.current.onMoveShape(current.id, { x: current.origin.x + dx / k, y: current.origin.y + dy / k });
    } else if (current.type === 'resize') {
      latest.current.onMoveShape(current.id, {
        width: clamp(current.origin.width + dx / k, SHAPE_MIN.width, SHAPE_MAX),
        height: clamp(current.origin.height + dy / k, SHAPE_MIN.height, SHAPE_MAX),
      });
    }
  };

  const isDoubleTap = (id) => {
    const now = Date.now();
    if (lastTap.current.id === id && now - lastTap.current.time < DOUBLE_TAP_MS) {
      lastTap.current = { id: null, time: 0 };
      return true;
    }
    lastTap.current = { id, time: now };
    return false;
  };

  const handlePointerUp = (event) => {
    if (!pointers.current.has(event.pointerId)) return;
    pointers.current.delete(event.pointerId);
    const current = gesture.current;

    if (current?.type === 'pinch') {
      gesture.current = pointers.current.size ? { type: 'idle' } : null;
      return;
    }
    if (pointers.current.size === 0) gesture.current = null;
    if (!current || current.moved || event.type === 'pointercancel') return;

    if (current.action) {
      if (Math.hypot(event.clientX - current.startX, event.clientY - current.startY) > ACTION_SLOP) return;
      if (current.action === 'toggle-collapse') latest.current.onToggleCollapse(current.id);
      if (current.action === 'toggle-item') latest.current.onToggleShapeItem(current.id, current.itemId);
      if (current.action === 'add-child') {
        flushSync(() => latest.current.onAddChild(current.id));
        focusEditor(true);
      }
      return;
    }

    if (current.type === 'pan') {
      latest.current.onSelect(null);
    } else if (current.type === 'node') {
      if (isDoubleTap(current.id)) {
        flushSync(() => latest.current.onEdit(current.id));
        focusEditor(true);
      } else {
        latest.current.onSelect(current.id);
      }
    } else if (current.type === 'shape' || current.type === 'resize') {
      if (isDoubleTap(current.id)) {
        flushSync(() => latest.current.onEditShape(current.id));
        focusEditor(false);
      } else {
        latest.current.onSelectShape(current.id);
      }
    }
  };

  const edges = useMemo(
    () =>
      visibleNodes
        .filter((node) => node.parentId != null)
        .map((node) => ({ node, parent: byId.get(node.parentId) }))
        .filter((edge) => edge.parent),
    [visibleNodes, byId]
  );

  const hiddenCounts = useMemo(
    () =>
      Object.fromEntries(
        visibleNodes
          .filter((node) => node.collapsed && childrenMap.has(node.id))
          .map((node) => [node.id, getDescendantIds(nodes, node.id, childrenMap).size])
      ),
    [visibleNodes, nodes, childrenMap]
  );

  return (
    <div
      ref={containerRef}
      className="mm-canvas"
      style={{
        backgroundSize: `${GRID * view.k}px ${GRID * view.k}px`,
        backgroundPosition: `${view.x}px ${view.y}px`,
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      <div className="mm-world" style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.k})` }}>
        {shapes.map((shape) => (
          <MindMapShape
            key={shape.id}
            shape={shape}
            selected={shape.id === selectedShapeId}
            editing={shape.id === editingShapeId}
            onChange={onShapeChange}
            onClose={onCommitEdit}
          />
        ))}
        <svg className="mm-edges" width="1" height="1" aria-hidden="true">
          {edges.map(({ node, parent }) => (
            <path key={node.id} className={`mm-edge ${colorClass(node.color)}`} d={edgePath(parent, node, sizes)} />
          ))}
        </svg>
        {visibleNodes.map((node) => {
          const parent = node.parentId == null ? null : byId.get(node.parentId);
          return (
            <MindMapNode
              key={node.id}
              node={node}
              isRoot={node.parentId == null}
              selected={node.id === selectedId}
              editing={node.id === editingId}
              side={parent && node.x < parent.x ? -1 : 1}
              hiddenCount={hiddenCounts[node.id] ?? 0}
              onSize={onSize}
              onText={onTextChange}
              onCommit={onCommitEdit}
              onAddChild={onAddChild}
            />
          );
        })}
      </div>
    </div>
  );
});

export default MindMapCanvas;
